'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { flushSync } from 'react-dom';

const NAV_CONTENT_SCROLL_TARGET = 'gk-nav-content-scroll-target';

export type SiteNavItem<T extends string = string> = {
  id: T;
  href: string;
  title: string;
};

function matchItemByPathname<T extends string>(
  items: readonly SiteNavItem<T>[],
  pathname: string,
): SiteNavItem<T> | undefined {
  const exact = items.find((item) => item.href === pathname);
  if (exact) return exact;
  // Prefer longer hrefs so /game/dguides wins over /game when both could match.
  return [...items]
    .sort((left, right) => right.href.length - left.href.length)
    .find(
      (item) =>
        pathname === item.href || pathname.startsWith(`${item.href}/`),
    );
}

/**
 * Channel navigation for wiki shells.
 *
 * - localTabs: SPA switch via history.pushState + local active state (no soft RSC).
 * - otherwise: hard navigation (location.assign). Soft RSC (`?_rsc`) hits Node
 *   under bots-only SSR and can 504 for real users.
 */
export function useSiteNavigation<T extends string>(
  initial: T,
  items: readonly SiteNavItem<T>[],
  localTabs = false,
) {
  const [active, setActive] = useState(initial);
  const marker = useRef<HTMLDivElement>(null);
  const pending = useRef<'content' | 'top' | null>(null);
  const [compact, setCompact] = useState(false);
  const compactRef = useRef(compact);
  compactRef.current = compact;

  // Sync from route-driven initial only when the URL already matches that tab
  // (hard land / Next pathname update). Avoid overwriting SPA pushState switches
  // while usePathname() is still stale.
  useEffect(() => {
    if (!localTabs) {
      setActive(initial);
      return;
    }
    const item = items.find((entry) => entry.id === initial);
    if (item && item.href === window.location.pathname) setActive(initial);
  }, [initial, items, localTabs]);

  useEffect(() => {
    if (!localTabs) return;
    const onPopState = () => {
      const matched = matchItemByPathname(items, window.location.pathname);
      if (matched) setActive(matched.id);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [items, localTabs]);

  const align = useCallback(() => {
    if (marker.current) {
      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY + marker.current.getBoundingClientRect().top + 2,
        ),
        behavior: 'instant',
      });
      setCompact(true);
    }
  }, []);

  useLayoutEffect(() => {
    let savedTarget: string | null = null;
    try {
      savedTarget = window.sessionStorage.getItem(NAV_CONTENT_SCROLL_TARGET);
      if (savedTarget) window.sessionStorage.removeItem(NAV_CONTENT_SCROLL_TARGET);
    } catch {
      // Preserve navigation behavior when session storage is unavailable.
    }
    if (savedTarget) {
      if (new URL(savedTarget, window.location.href).pathname === window.location.pathname)
        pending.current = 'content';
    }
    const position = pending.current;
    pending.current = null;
    const frame = window.requestAnimationFrame(() => {
      if (position === 'content') align();
      else if (position === 'top')
        window.scrollTo({ top: 0, behavior: 'instant' });
    });
    const node = marker.current;
    if (!node) return () => window.cancelAnimationFrame(frame);
    const mobileHeaderOffset = window.matchMedia('(max-width: 760px)').matches
      ? 54
      : 0;
    const syncCompact = (event?: Event | IntersectionObserverEntry[]) => {
      const next = node.getBoundingClientRect().top <= mobileHeaderOffset;
      if (compactRef.current === next) return;
      compactRef.current = next;
      if (event instanceof Event) {
        flushSync(() => setCompact(next));
      } else {
        setCompact(next);
      }
    };
    syncCompact();
    window.addEventListener('scroll', syncCompact, { passive: true });
    document.addEventListener('scroll', syncCompact, { passive: true, capture: true });
    const observer = new IntersectionObserver(
      syncCompact,
      { rootMargin: `-${mobileHeaderOffset}px 0px 0px` },
    );
    observer.observe(node);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', syncCompact);
      document.removeEventListener('scroll', syncCompact, true);
      observer.disconnect();
    };
  }, [active, align]);

  const go = useCallback(
    (next: T, position: 'preserve' | 'content' | 'top' = 'preserve') => {
      const item = items.find((entry) => entry.id === next);
      if (!item) return;
      if (next === active && item.href === window.location.pathname) {
        if (position === 'content') align();
        else if (position === 'top')
          window.scrollTo({ top: 0, behavior: 'instant' });
        return;
      }
      pending.current = position === 'preserve' ? null : position;
      if (localTabs) {
        if (item.href !== window.location.pathname) {
          window.history.pushState(null, '', item.href);
        }
        setActive(next);
        return;
      }
      if (position === 'content') {
        try {
          window.sessionStorage.setItem(NAV_CONTENT_SCROLL_TARGET, item.href);
        } catch {
          // Full navigation remains usable if storage is unavailable.
        }
      }
      window.location.assign(item.href);
    },
    [active, align, items, localTabs],
  );

  const navigate = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, next: T) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return false;
      event.preventDefault();
      const isPinned = (marker.current?.getBoundingClientRect().top ?? 1) < 0;
      go(next, isPinned ? 'content' : 'preserve');
      return true;
    },
    [go],
  );

  return { active, compact, marker, align, go, navigate };
}
